/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentPropsWithoutRef, forwardRef, type Ref } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		borderRadius: token('radius.full'),
		padding: '0 .3em 2px .23em',
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.neutral'),
		userSelect: 'none',
		whiteSpace: 'pre-wrap',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	// NOTE: This duplicates FocusRing styles from `@atlaskit/focus-ring` and may want to be replaced by a `Focusable` primitive
	focusRing: {
		'&:focus, &:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('space.025'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:focus:not(:focus-visible)': {
			outline: 'none',
		},
		'@media screen and (forced-colors: active), screen and (-ms-high-contrast: active)': {
			'&:focus-visible': {
				outline: '1px solid',
			},
		},
	},
});

export type MentionProps = ComponentPropsWithoutRef<'span'> & {
	name: string;
	testId?: string;
};

export const Mention: import('react').ForwardRefExoticComponent<
	Omit<
		import('react').DetailedHTMLProps<
			import('react').HTMLAttributes<HTMLSpanElement>,
			HTMLSpanElement
		>,
		'ref'
	> & {
		name: string;
		testId?: string;
	} & import('react').RefAttributes<HTMLSpanElement>
> = forwardRef(
	({ name, prefix = '@', testId, ...htmlAttributes }: MentionProps, ref?: Ref<HTMLSpanElement>) => {
		return (
			<span
				{...htmlAttributes}
				css={[styles.root, styles.focusRing]}
				data-testid={testId}
				ref={ref}
			>
				{prefix}
				{name}
			</span>
		);
	},
);
